import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api.ts';
import { useT, useUiLocale } from '../i18n.ts';
import { Dialog } from './Dialog.tsx';
import { Seal } from './common.tsx';
import { GlossaryTab } from './GlossaryTab.tsx';
import { InviteShare } from './InviteShare.tsx';
import type { InviteLang } from '@tc/domain';
import { SuggestionsTab } from './Suggestions.tsx';
import { DiagReports } from './DiagReports.tsx';
import { CostsTab } from './CostsTab.tsx';
import { useStore } from '../store.ts';

interface Org { id: string; name: string; isHost: boolean; userCount: number }
interface Invite { id: string; email: string | null; isAdmin: boolean; organizationName: string; createdAt: string; expiresAt: string; usedAt: string | null; usedByName: string | null; revokedAt: string | null }
interface AdminUser { id: string; displayName: string; email: string; organizationName: string | null; readingLocale: string; disabled: boolean; isAdmin: boolean; resetRequestedAt: string | null; mfa: boolean }
interface Usage { days: Array<{ day: string; provider: string; units: number; calls: number; failures: number }>; caps: { daily: number; monthly: number }; queue: Record<string, number> }

export function AdminDialog({ onClose, initialTab }: { onClose: () => void; initialTab?: 'users' }) {
  const t = useT();
  const ui = useUiLocale();
  const [tab, setTab] = useState<'invites' | 'users' | 'glossary' | 'suggestions' | 'usage' | 'costs' | 'diag'>(initialTab ?? 'invites');
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [link, setLink] = useState<{ url: string; expiresAt?: string; kind: 'invite' | 'reset'; forHost?: boolean; mail?: { to: string; ok: boolean } } | null>(null);
  const me = useStore((s) => s.me);
  const [form, setForm] = useState({ organizationId: '', newOrganizationName: '', email: '', isAdmin: false, sendEmail: true, emailLang: '' as '' | InviteLang });
  const [formError, setFormError] = useState<string | null>(null);
  const hostSelected = orgs.some((o) => o.id === form.organizationId && o.isHost);
  // Language of the invitation e-mail: Swedish for our own staff, Chinese for suppliers, unless chosen.
  const emailLang: InviteLang = form.emailLang || (hostSelected ? 'sv-SE' : 'zh-Hans');

  const refresh = useCallback(async () => {
    const [o, i, u, us] = await Promise.all([
      api<Org[]>('GET', '/api/admin/organizations'),
      api<Invite[]>('GET', '/api/admin/invites'),
      api<AdminUser[]>('GET', '/api/admin/users'),
      api<Usage>('GET', '/api/admin/usage'),
    ]);
    setOrgs(o);
    setInvites(i);
    setUsers(u);
    setUsage(us);
    setForm((f) => (f.organizationId ? f : { ...f, organizationId: o[0]?.id ?? 'new' }));
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const fmt = (iso: string) => new Date(iso).toLocaleString(ui === 'sv-SE' ? 'sv-SE' : ui === 'zh-Hans' ? 'zh-CN' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' });

  const createInvite = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const body = {
      ...(form.organizationId === 'new' ? { newOrganizationName: form.newOrganizationName } : { organizationId: form.organizationId }),
      ...(form.email ? { email: form.email, sendEmail: form.sendEmail, emailLang } : {}),
      isAdmin: form.isAdmin && hostSelected,
    };
    let r: { token: string; expiresAt: string; emailed?: boolean };
    try {
      r = await api<{ token: string; expiresAt: string; emailed?: boolean }>('POST', '/api/admin/invites', body);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'error';
      setFormError(code === 'admin_email_not_allowed' || code === 'admin_only_in_host_org' ? t('adminNotAllowed') : code === 'email_taken' ? t('emailTaken') : t('inviteFailed'));
      return;
    }
    setLink({
      url: `${location.origin}/invite/${r.token}`,
      expiresAt: r.expiresAt,
      kind: 'invite',
      forHost: hostSelected,
      mail: form.email && form.sendEmail ? { to: form.email, ok: !!r.emailed } : undefined,
    });
    setForm({ ...form, email: '', newOrganizationName: '', isAdmin: false, emailLang: '' });
    await refresh();
  };

  const inviteStatus = (i: Invite) =>
    i.usedAt ? t('status_used', { name: i.usedByName ?? '' }) : i.revokedAt ? t('status_revoked') : new Date(i.expiresAt) < new Date() ? t('status_expired') : t('status_open', { date: fmt(i.expiresAt) });

  const todayUnits = usage?.days.filter((d) => d.day === new Date().toISOString().slice(0, 10)).reduce((n, d) => n + d.units, 0) ?? 0;

  return (
    <Dialog title={t('admin')} onClose={onClose} wide>
      <p className="muted small">{t('noPrivateAccess')}</p>
      <div className="segmented" role="tablist">
        {(['invites', 'users', 'glossary', 'suggestions', 'usage', 'costs', 'diag'] as const).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{t(k)}</button>
        ))}
      </div>

      {link && (
        <div className="link-box" role="status">
          <p>{link.kind === 'invite' ? t('inviteCreated', { date: link.expiresAt ? fmt(link.expiresAt) : '' }) : t('resetCreated')}</p>
          {link.mail && (
            <p className={link.mail.ok ? 'ok small' : 'form-error small'} role="status" data-testid="invite-mailed">
              {link.mail.ok ? t('inviteMailed', { to: link.mail.to }) : t('inviteMailFailed', { to: link.mail.to })}
            </p>
          )}
          <div className="row">
            <input readOnly value={link.url} onFocus={(e) => e.target.select()} aria-label={t('copyLink')} />
            <button className="btn small" onClick={() => void navigator.clipboard?.writeText(link.url)}>{t('copyLink')}</button>
          </div>
          {link.kind === 'invite' && (
            <InviteShare
              url={link.url}
              hours={link.expiresAt ? Math.max(1, Math.round((new Date(link.expiresAt).getTime() - Date.now()) / 3600_000)) : 72}
              sender={me?.displayName ?? ''}
              org={me?.organizationName ?? ''}
              defaultLang={link.forHost ? 'sv-SE' : 'zh-Hans'}
            />
          )}
        </div>
      )}

      {tab === 'invites' && (
        <>
          <form className="stack invite-form" onSubmit={createInvite}>
            <label className="field">
              <span>{t('organization')}</span>
              <select value={form.organizationId} onChange={(e) => setForm({ ...form, organizationId: e.target.value })}>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                <option value="new">{t('newOrganization')}</option>
              </select>
            </label>
            {form.organizationId === 'new' && (
              <label className="field">
                <span>{t('newOrganizationName')}</span>
                <input required value={form.newOrganizationName} onChange={(e) => setForm({ ...form, newOrganizationName: e.target.value })} />
              </label>
            )}
            <label className="field">
              <span>{t('emailOptional')}</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            {form.email && (
              <div className="row wrap invite-mail">
                <label className="check">
                  <input type="checkbox" checked={form.sendEmail} onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })} />
                  <span>{t('inviteSendEmail')}</span>
                </label>
                {form.sendEmail && (
                  <select value={emailLang} onChange={(e) => setForm({ ...form, emailLang: e.target.value as InviteLang })} aria-label={t('inviteLanguage')}>
                    <option value="zh-Hans">简体中文</option>
                    <option value="sv-SE">Svenska</option>
                    <option value="en">English</option>
                  </select>
                )}
              </div>
            )}
            {hostSelected && (
              <label className="check">
                <input type="checkbox" checked={form.isAdmin} onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })} />
                <span>{t('asAdmin')}</span>
              </label>
            )}
            {formError && <p className="form-error" role="alert">{formError}</p>}
            <button className="btn primary">{t('createInvite')}</button>
          </form>
          <table className="table">
            <tbody>
              {invites.map((i) => (
                <tr key={i.id}>
                  <td>{i.organizationName}</td>
                  <td>{i.email ?? '—'}</td>
                  <td className="muted">{inviteStatus(i)}</td>
                  <td>
                    {!i.usedAt && !i.revokedAt && new Date(i.expiresAt) > new Date() && (
                      <button className="link-btn danger" onClick={() => void api('DELETE', `/api/admin/invites/${i.id}`).then(refresh)}>{t('revoke')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {tab === 'users' && (
        <table className="table">
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.disabled ? 'muted' : ''}>
                <td><Seal locale={u.readingLocale} small /></td>
                <td>
                  {u.displayName}
                  {u.isAdmin && <span className="tag">{t('admin')}</span>}
                  {u.resetRequestedAt && <span className="tag warn">{t('resetRequested')}</span>}
                  {u.mfa && <span className="tag">{t('mfaTag')}</span>}
                  <div className="muted small">{u.email}</div>
                </td>
                <td>{u.organizationName}</td>
                <td className="actions">
                  <button
                    className="link-btn"
                    onClick={() =>
                      void api<{ token: string }>('POST', `/api/admin/users/${u.id}/password-reset`).then((r) => {
                        setLink({ url: `${location.origin}/reset/${r.token}`, kind: 'reset' });
                        void refresh();
                      })
                    }
                  >
                    {t('resetLink')}
                  </button>
                  {u.mfa && u.id !== me?.id && (
                    <button
                      className="link-btn"
                      onClick={() => {
                        if (confirm(t('mfaAdminResetConfirm', { name: u.displayName }))) void api('POST', `/api/admin/users/${u.id}/mfa-reset`).then(refresh);
                      }}
                    >
                      {t('mfaAdminReset')}
                    </button>
                  )}
                  <button className="link-btn danger" onClick={() => void api('PATCH', `/api/admin/users/${u.id}`, { disabled: !u.disabled }).then(refresh)}>
                    {u.disabled ? t('enable') : t('disable')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === 'glossary' && <GlossaryTab />}

      {tab === 'suggestions' && <SuggestionsTab />}
      {tab === 'diag' && <DiagReports />}
      {tab === 'costs' && <CostsTab />}
      {tab === 'usage' && usage && (
        <div className="stack">
          <dl className="stats">
            <div><dt>{t('todayChars')}</dt><dd>{todayUnits.toLocaleString()}</dd></div>
            <div><dt>{t('dailyCap')}</dt><dd>{usage.caps.daily.toLocaleString()}</dd></div>
            <div><dt>{t('monthCap')}</dt><dd>{usage.caps.monthly.toLocaleString()}</dd></div>
            <div><dt>{t('queue')}</dt><dd>{Object.entries(usage.queue).map(([k, v]) => `${k} ${v}`).join(', ') || '0'}</dd></div>
          </dl>
          <table className="table">
            <tbody>
              {usage.days.map((d) => (
                <tr key={`${d.day}-${d.provider}`}>
                  <td>{d.day}</td>
                  <td>{d.provider}</td>
                  <td className="num">{d.units.toLocaleString()}</td>
                  <td className="num">{d.calls}</td>
                  <td className="num">{d.failures}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}
